import { useState } from "react";
import { Button, Drawer, Grid } from "@pushpress/shared-components";
import { RadioGroup, RadioGroupItem, Label } from "@pushpress/pushpress-ui";
import { AlertTriangle, Building, CreditCard } from "lucide-react";
import { toast } from "react-toastify";
import { DrawerPanel } from "../../../../components/common/DrawerPanel";
import { useQueryClient } from "@tanstack/react-query";
import { DATA_NOT_AVAILABLE } from "../../constants";
import { usePostCreateMultipleBillingRecords } from "../../api/migrationAssistantApi.hooks";
import { defaultToastOptions } from "../../../../utils/misc";
import {
  MemberDataType,
  StripePaymentData,
  StripePaymentMethodDataType,
} from "../../types";

export default function MultiPaymentsDrawer({
  selectedMemberId,
  showMultiPaymentsDrawer,
  setShowMultiPaymentsDrawer,
  stripeBillingData,
  membersData,
}: {
  selectedMemberId: number;
  showMultiPaymentsDrawer: boolean;
  setShowMultiPaymentsDrawer: (value: boolean) => void;
  stripeBillingData: StripePaymentData | string;
  membersData: MemberDataType[];
}) {
  return (
    <Drawer
      visible={showMultiPaymentsDrawer}
      anchor="right"
      width="400px"
      disableClickAway
      disableEscapeKeyDown
    >
      <DrawerContent />
    </Drawer>
  );

  function DrawerContent() {
    const queryClient = useQueryClient();
    const [selectedPrimaryPayment, setSelectedPrimaryPayment] = useState("");

    const {
      mutate: acceptPaymentMethods,
      isLoading: acceptPaymentMethodLoading,
    } = usePostCreateMultipleBillingRecords();

    const member = membersData?.find(
      (member) => member.id === selectedMemberId,
    );
    const paymentMethods: StripePaymentMethodDataType[] =
      (typeof stripeBillingData === "object" &&
        stripeBillingData &&
        member?.stripeId &&
        stripeBillingData[member.stripeId]?.paymentMethods?.data) ||
      [];

    const isCardExpired = (expMonth: number, expYear: number): boolean => {
      const expirationTime = new Date(expYear, expMonth - 1).getTime();
      return Date.now() > expirationTime;
    };

    const getIcon = (type: string) => {
      if (type === "card") {
        return <CreditCard className="tw-h-5 tw-w-5" />;
      }
      if (type === "us_bank_account") {
        return <Building className="tw-h-5 tw-w-5" />;
      }
      return null;
    };

    const handleAcceptPaymentMethod = () => {
      // Filter out expired card payment methods
      const paymentData = paymentMethods
        ?.filter((paymentMethod) =>
          paymentMethod.card
            ? !isCardExpired(
                paymentMethod.card.exp_month,
                paymentMethod.card.exp_year,
              )
            : true,
        )
        ?.map((paymentMethod) => ({
          userId: selectedMemberId,
          userUuid: member.userUuid,
          customerReference: member?.stripeId,
          paymentReference: paymentMethod.id,
          isPermanent: paymentMethod.id === selectedPrimaryPayment,
        }));

      acceptPaymentMethods(
        {
          data: paymentData,
        },
        {
          onSuccess: () => {
            toast.success("Payment methods accepted", defaultToastOptions);
            queryClient.invalidateQueries({
              queryKey: ["fetchMigratingMembers"],
            });
            setShowMultiPaymentsDrawer(false);
          },
          onError: () => {
            toast.error(
              "There was an error accepting payment methods",
              defaultToastOptions,
            );
          },
        },
      );
    };

    const getPaymentMethodDetails = (method: StripePaymentMethodDataType) => {
      if (method.card) {
        const brandName = `${method.card.brand
          .slice(0, 1)
          .toUpperCase()}${method.card.brand.slice(1).toLowerCase()} `;
        return `${brandName} •••• ${method.card.last4}`;
      }

      if (method.us_bank_account) {
        const brandName = `${method.us_bank_account.bank_name
          .slice(0, 1)
          .toUpperCase()}${method.us_bank_account.bank_name
          .slice(1)
          .toLowerCase()} `;
        return `${brandName} •••• ${method.us_bank_account.last4}`;
      }
      return DATA_NOT_AVAILABLE;
    };

    return (
      <DrawerPanel
        title="Select payment"
        onClose={() => setShowMultiPaymentsDrawer(false)}
        loading={acceptPaymentMethodLoading}
      >
        <Grid container>
          <Grid item xs={12}>
            {paymentMethods?.length ? (
              <RadioGroup
                value={selectedPrimaryPayment}
                onValueChange={setSelectedPrimaryPayment}
              >
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className="tw-flex tw-items-center tw-space-x-2 tw-mb-4"
                  >
                    <RadioGroupItem
                      value={method.id}
                      id={method.id}
                      disabled={
                        method.card &&
                        isCardExpired(
                          method.card.exp_month,
                          method.card.exp_year,
                        )
                      }
                    />
                    <Label
                      htmlFor={method.id}
                      className="tw-flex tw-items-center tw-space-x-2 tw-cursor-pointer"
                    >
                      {getIcon(method.card ? "card" : "us_bank_account")}
                      <div className="tw-flex-grow">
                        <p className="tw-text-sm tw-text-gray-500">
                          {getPaymentMethodDetails(method)}
                        </p>
                        {method.card &&
                          method.card.exp_month &&
                          method.card.exp_year && (
                            <p className="tw-text-sm tw-text-gray-500">
                              Expires: {method.card.exp_month}/
                              {method.card.exp_year}
                            </p>
                          )}
                      </div>
                      {method.card &&
                        isCardExpired(
                          method.card.exp_month,
                          method.card.exp_year,
                        ) && (
                          <AlertTriangle
                            data-testid="expired-icon"
                            className="tw-h-5 tw-w-5 tw-text-red-500"
                            aria-label="Expired"
                          />
                        )}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            ) : (
              <p className="tw-text-sm tw-text-gray-500">
                {DATA_NOT_AVAILABLE}
              </p>
            )}
          </Grid>
          <Grid item xs={12} marginY={4}>
            <Button
              text="Save"
              buttonType="primary"
              size="large"
              fullWidth
              disabled={!selectedPrimaryPayment}
              onClick={() => handleAcceptPaymentMethod()}
            />
          </Grid>
        </Grid>
      </DrawerPanel>
    );
  }
}
